import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  computed,
  DEFAULT_CURRENCY_CODE,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  provideZoneChangeDetection,
  Signal,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';

import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { TimepickerConfig } from 'ngx-bootstrap/timepicker';
import { CookieModule } from 'ngx-cookie';
import { provideHighlightOptions } from 'ngx-highlightjs';
import { provideMatomo } from 'ngx-matomo-client';

import { AuthHeaderInterceptor, ErrorInterceptor } from '@bn/auth/interceptors';
import { UserDetailsStateService } from '@bn/auth/services';
import { UserStateService } from '@bn/auth/stores';
import { OverviewService } from '@bn/features/overview/services/overview.service';
import { provideUserSettingsTheme } from '@bn/features/theme/services';
import { BASE_PATH as AI_GATEWAY_BASE_PATH } from '@bn/openapi/ai-gateway';
import { BASE_PATH as CORE_BASE_PATH } from '@bn/openapi/core';
import { APP_ENVIRONMENT as EDGEDB_APP_ENVIRONMENT, BASE_PATH as EDGEDB_BASE_PATH } from '@bn/openapi/database';
import { BASE_PATH as LOAD_BALANCER_BASE_PATH } from '@bn/openapi/load-balancer';
import { BASE_PATH } from '@bn/openapi/mc-logging';
import { BASE_PATH as MC_BASE_PATH } from '@bn/openapi/mc-v3';
import { BASE_PATH as ORIGIN_LOGS_BASE_PATH } from '@bn/openapi/origin-logs';
import { BASE_PATH as PARTNER_PORTAL_BASE_PATH } from '@bn/openapi/partner-portal';
import { BASE_PATH as SHIELD_BASE_PATH, SHIELD_LOGS_PATH } from '@bn/openapi/shield';
import { BASE_PATH as STREAM_STATISTICS_BASE_PATH } from '@bn/openapi/stream-statistics';
import { BASE_PATH as STREAM_VIDEO_BASE_PATH, Configuration, CONFIGURATION_PATH } from '@bn/openapi/stream-video';
import {
  ANALYTICS_USER,
  AnalyticsUser,
  APP_CONFIG,
  AppConfig,
  CLIENT_IP,
  ENV_CONFIG,
  EnvConfig,
  EXT_URL_CONFIG,
  ExternalUrlConfig,
  FAVOURITES,
  GEO_DATA,
} from '@bn/primitives';
import { AppStore } from '@bn/product-stores/app';
import { HttpErrorInterceptor } from '@bn/shared/core/interceptors';
import { FormatBytePipe, VatPipe } from '@bn/shared/core/pipes';
import { BnTemplatePageTitleStrategy, VersionCheckService } from '@bn/shared/core/services';
import { MENU_ACCESS, MixpanelEventsDirective } from '@bn/shared/directives';
import { fontawesomeProviders } from '@bn/shared/font-awesome';
import { provideWebUi } from '@bn/web-ui';

import { environment } from '../environments/environment';

import { appRoutes } from './app-routing.module';
import { AppConfiguration, ExtUrlConfiguration } from './config';

export const appConfig: EnvironmentProviders = makeEnvironmentProviders([
  DecimalPipe,
  CurrencyPipe,
  FormatBytePipe,
  VatPipe,
  // CookieService has no providedIn, so ngx-cookie's module must supply it app-wide:
  // AuthService (and through it UserDetailsStateService) injects it during bootstrap.
  importProvidersFrom(CookieModule.withOptions()),
  // Injectable-only: root-provided so services (not just page components) can track events.
  MixpanelEventsDirective,
  provideTranslateService({
    loader: provideTranslateHttpLoader({ prefix: '/assets/i18n/', suffix: '.json', enforceLoading: false }),
    fallbackLang: 'en',
    lang: 'en',
  }),
  provideZoneChangeDetection({ eventCoalescing: true }),
  { provide: AI_GATEWAY_BASE_PATH, useValue: environment.aiGatewayApiUrl },
  { provide: CORE_BASE_PATH, useValue: environment.apiUrl },
  { provide: DEFAULT_CURRENCY_CODE, useFactory: (APP_CONFIG: AppConfig): string => APP_CONFIG.currencyCode, deps: [APP_CONFIG] },
  { provide: EDGEDB_BASE_PATH, useValue: environment.apiEdgeDbLiteUrl },
  { provide: EDGEDB_APP_ENVIRONMENT, useValue: environment },
  { provide: LOAD_BALANCER_BASE_PATH, useValue: environment.apiUrl },
  { provide: ORIGIN_LOGS_BASE_PATH, useValue: environment.cdnOriginErrorLogsUrl },
  { provide: PARTNER_PORTAL_BASE_PATH, useValue: environment.apiUrl },
  { provide: MC_BASE_PATH, useValue: environment.apiMcUrl },
  { provide: SHIELD_BASE_PATH, useValue: environment.apiUrl },
  { provide: SHIELD_LOGS_PATH, useValue: environment.shieldLogsUrl },
  { provide: STREAM_VIDEO_BASE_PATH, useValue: environment.streamApiUrl },
  { provide: STREAM_STATISTICS_BASE_PATH, useValue: environment.streamStatisticsApiUrl },
  {
    provide: CONFIGURATION_PATH,
    useFactory: (basePath: string): Configuration => new Configuration({ basePath }),
    deps: [STREAM_VIDEO_BASE_PATH],
  },
  { provide: BASE_PATH, useValue: environment.mcLoggingUrl },
  { provide: APP_CONFIG, useFactory: (ENV_CONFIG: EnvConfig): AppConfig => new AppConfiguration(ENV_CONFIG).getConfig(), deps: [ENV_CONFIG] },
  { provide: ENV_CONFIG, useValue: environment },
  { provide: EXT_URL_CONFIG, useFactory: (ENV_CONFIG: EnvConfig): ExternalUrlConfig => new ExtUrlConfiguration(ENV_CONFIG).getUrls(), deps: [ENV_CONFIG] },
  {
    provide: CLIENT_IP,
    useFactory: (userState: UserStateService): Signal<string | undefined> => computed(() => userState.userStore.clientInfo()?.IpAddress),
    deps: [UserStateService],
  },
  { provide: FAVOURITES, useExisting: OverviewService },
  { provide: GEO_DATA, useExisting: AppStore },
  { provide: MENU_ACCESS, useExisting: UserDetailsStateService },
  // MixpanelEventsDirective lives in a type:util lib and cannot read UserStateService itself.
  {
    provide: ANALYTICS_USER,
    useFactory: (userState: UserStateService) => (): AnalyticsUser => userState.userStore.userDetails(),
    deps: [UserStateService],
  },
  // Backs web-ui's ThemeService with the account-level theme from /v2/user/settings.
  provideUserSettingsTheme(),
  { provide: HTTP_INTERCEPTORS, useClass: AuthHeaderInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
  { provide: TitleStrategy, useClass: BnTemplatePageTitleStrategy },
  // Statistics is the only timepicker usage: 24h, hours-only (no meridian, minutes or seconds).
  {
    provide: TimepickerConfig,
    useFactory: (): TimepickerConfig => Object.assign(new TimepickerConfig(), { showMeridian: false, showMinutes: false, showSeconds: false }),
  },
  provideAppInitializer(() => {
    // Instantiating VersionCheckService is the whole point: it starts the version poll.
    inject(VersionCheckService);
  }),
  fontawesomeProviders.provideFontawesome(),
  provideWebUi(),
  provideMatomo({
    disabled: !environment.production,
    runOutsideAngularZone: true,
    siteId: 1,
    trackerUrl: 'https://matomo.bunny.net',
  }),
  provideHighlightOptions({
    coreLibraryLoader: () => import('highlight.js/lib/core'),
    languages: {
      typescript: () => import('highlight.js/lib/languages/typescript'),
    },
  }),
  provideRouter(
    appRoutes,
    withInMemoryScrolling({
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'disabled',
    }),
    // withDebugTracing(),
    // withViewTransitions({
    //   onViewTransitionCreated: ({ transition, to }) => {
    //     const router = inject(Router);
    //     const toTree = createUrlTreeFromSnapshot(to, []);
    //     // Skip the transition if the only thing changing is the fragment and queryParams
    //     if (
    //       router.isActive(toTree, {
    //         paths: 'exact',
    //         matrixParams: 'exact',
    //         fragment: 'ignored',
    //         queryParams: 'ignored',
    //       })
    //     ) {
    //       transition.skipTransition();
    //     }
    //   },
    // }),
    // withViewTransitions(), // removed: Angular 20 logs AbortError in dev mode by design; DOM updates too slow for transition window
    withComponentInputBinding(),
    withRouterConfig({
      paramsInheritanceStrategy: 'always',
      onSameUrlNavigation: 'reload',
    }),
    withPreloading(PreloadAllModules),
  ),
  // importProvidersFrom(ApiModule.forRoot({})),
  provideHttpClient(withInterceptorsFromDi()),
]);
